<template>
  <KekkleBottomSheet
    v-model:open="isOpen"
    shadow="purple"
    padding="normal"
    initial-height="full"
  >
    <div class="dark:text-slate-200">
      <!-- Header -->
      <div class="mb-6 flex items-start justify-between gap-4">
        <h2 class="flex-1 text-2xl font-bold">{{ $t("pages.photos.edit") }}</h2>
        <default-button
          color="blue"
          :disabled="!validated || status.deleting"
          :loading="status.updating"
          @click="update"
        >
          {{ $t("global.inputs.labels.update") }}
        </default-button>
      </div>

      <!-- Form -->
      <div class="mb-4 w-full">
        <text-area-input
          v-model="caption"
          :has-error="validationErrors.caption"
          :error-message="$t('global.inputs.errors.photo_caption')"
          :label="$t('global.inputs.labels.photo_description') + '*'"
          :placeholder="$t('global.inputs.placeholders.photo_description')"
        />
      </div>

      <p class="dark:text-slate-200">
        {{ $t("pages.photo.date_time_of_picture") }}
        {{ timestamp ? dateTimeAgo(timestamp) : "" }}
      </p>

      <div class="mb-4 w-full">
        <p class="font-medium dark:text-slate-200">
          {{ $t("global.inputs.labels.date_time") }} *
        </p>
        <p
          v-show="validationErrors.timestamp"
          class="mt-1 text-sm text-red-600"
        >
          {{ $t("global.inputs.errors.date_time") }}
        </p>
        <div class="flex w-full flex-col items-center">
          <date-input v-if="timestamp" v-model="timestamp" :date-time="true" />
        </div>
      </div>

      <!-- Delete Section -->
      <div v-if="!showDeleteConfirmation" class="mb-4 w-full">
        <default-button
          color="red"
          :disabled="status.updating"
          :loading="status.deleting"
          @click="showDeleteConfirmation = true"
        >
          <p>{{ $t("global.inputs.labels.delete_photo") }}</p>
        </default-button>
      </div>

      <!-- Inline Delete Confirmation -->
      <inline-delete-confirmation
        v-else
        color="purple"
        :confirm-text="$t('pages.photo.confirm_delete_text')"
        :cancel-text="$t('pages.photo.confirm_close')"
        :delete-text="$t('pages.photo.confirm_delete')"
        :loading="status.deleting"
        @cancel="showDeleteConfirmation = false"
        @confirm="deletePhoto"
      />
    </div>
  </KekkleBottomSheet>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  setSuccessToast,
  setWarningToast,
} from "@/helpers/notifications/toast";
import TextAreaInput from "@/components/Inputs/TextAreaInput.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import DateInput from "@/components/Inputs/DateInput.vue";
import KekkleBottomSheet from "@/components/ui/KekkleBottomSheet.vue";
import InlineDeleteConfirmation from "@/components/InlineDeleteConfirmation.vue";
import { dateObjectToDateString } from "@/helpers/converts/date-converter";
import { dateTimeAgo as formatDateTimeAgo } from "@/helpers/dates/date-time-ago";
import { usePhotosStore } from "@/stores/modules/photos/photos";

type PhotoData = {
  _id: string;
  caption: string;
  timestamp: Date;
};

type Props = {
  photoData: PhotoData | null;
};

const props = defineProps<Props>();
const { t } = useI18n();
const photosStore = usePhotosStore();

const isOpen = defineModel<boolean>("open", { required: true });

// Form state
const status = ref({
  updating: false,
  deleting: false,
});
const caption = ref("");
const timestamp = ref<string | null>(null);
const showDeleteConfirmation = ref(false);
const photoId = ref<string | null>(null);

// Computed properties
const timestampValid = computed(() => !!timestamp.value);

const captionValid = computed(() => {
  if (caption.value) {
    return caption.value.length < 350;
  }
  return true;
});

const validationErrors = computed(() => ({
  caption: !captionValid.value,
  timestamp: !timestampValid.value,
}));

const validated = computed(() => {
  return !Object.values(validationErrors.value).some((value) => value === true);
});

// Methods
function loadPhotoData(data: PhotoData) {
  if (!data) return;

  photoId.value = data._id;
  caption.value = data.caption || "";
  timestamp.value = dateObjectToDateString(data.timestamp, "yyyy-mm-ddThh:mm");
}

function dateTimeAgo(date: string | Date): string {
  if (date) {
    return formatDateTimeAgo(new Date(date));
  }
  return "";
}

async function update() {
  status.value.updating = true;

  if (!validated.value) {
    setWarningToast({
      message: t("global.toast.check_all_fields"),
    });
    status.value.updating = false;
    return;
  }

  try {
    await photosStore.updatePhoto(photoId.value!, {
      caption: caption.value,
      timestamp: new Date(timestamp.value!),
    });

    setSuccessToast({
      message: t("global.toast.photo_saved"),
      emoji: "📸",
    });
    isOpen.value = false;
  } catch (e) {
    console.error("Error updating photo:", e);
  } finally {
    status.value.updating = false;
  }
}

async function deletePhoto() {
  try {
    status.value.deleting = true;

    await photosStore.deletePhoto(photoId.value!);

    setSuccessToast({
      message: t("global.toast.deleted_successfully"),
      emoji: "🗑️",
    });
    isOpen.value = false;
  } catch (e) {
    console.error("Error deleting photo:", e);
  } finally {
    status.value.deleting = false;
  }
}

// Watchers
watch(isOpen, (newValue) => {
  if (!newValue) {
    // Reset delete confirmation when closing
    showDeleteConfirmation.value = false;
  } else if (newValue && props.photoData) {
    // Load data when opening
    loadPhotoData(props.photoData);
  }
});

watch(
  () => props.photoData,
  (newData) => {
    if (newData && isOpen.value) {
      loadPhotoData(newData);
    }
  },
  { immediate: true },
);
</script>
